/**
 * The report could not be read (PROD-3204, PM slice 4, board N7). The server has attached the report to the request
 * and moved it to Result received; the values are entered by hand, or a clearer copy is uploaded. When the reading
 * service itself did not answer, "Read again" replaces "Upload a clearer copy". Behind `?x=intake-desk`.
 *
 * Pieces: the app `Dialog` at `--dialog-width-confirm` (400) and `Button` (the other way outline; Enter results primary
 * and last).
 */
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRetryDeskRun } from '@/intake/hooks/use-desk-intake';
import { isServiceFailure, notReadText } from '@/intake/lib/desk-intake-view';
import type { LabRequestIntake } from '@/intake/types';
import type { PatientLabOrderDetail } from '@/types/scheduling-api';

export interface ReportNotReadDialogProps {
  /** The row and its failed run; null closes the dialog. */
  target: { row: PatientLabOrderDetail; intake: LabRequestIntake } | null;
  onClose: () => void;
  onUploadAgain: (row: PatientLabOrderDetail) => void;
  onEnterResults: (row: PatientLabOrderDetail) => void;
}

export function ReportNotReadDialog({ target, onClose, onUploadAgain, onEnterResults }: ReportNotReadDialogProps) {
  const retry = useRetryDeskRun();
  const service = isServiceFailure(target?.intake.failureCode);
  const close = () => {
    retry.reset();
    onClose();
  };
  return (
    <Dialog open={target !== null} onOpenChange={(open) => (open ? undefined : close())}>
      <DialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <DialogHeader>
          <DialogTitle className="[font:var(--type-card-title)]">The report could not be read</DialogTitle>
          <DialogDescription className="break-words text-text-secondary [font:var(--type-meta)]">
            {target ? [target.row.patientName, target.row.orderName, target.intake.fileName].filter(Boolean).join(' · ') : ''}
          </DialogDescription>
        </DialogHeader>
        <p className="m-0 text-text-primary [font:var(--type-body)]">{notReadText(target?.intake.failureCode, !!target?.intake.patientDocumentId)}</p>
        {retry.isError ? (
          <p role="alert" className="m-0 text-[var(--status-bad)] [font:var(--type-body)]">
            Reading could not start again. Try again later, or enter the values by hand.
          </p>
        ) : null}
        <DialogFooter>
          {service ? (
            <Button
              variant="outline"
              disabled={retry.isPending}
              onClick={() =>
                target?.row.patientId &&
                retry.mutate({ patientId: target.row.patientId, runId: target.intake.runId }, { onSuccess: close })
              }
            >
              {retry.isPending ? 'Starting…' : 'Read again'}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => target && onUploadAgain(target.row)}>
              Upload a clearer copy
            </Button>
          )}
          <Button onClick={() => target && onEnterResults(target.row)}>Enter results</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
